import EmojiEventsOutlined from '@mui/icons-material/EmojiEventsOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { GOLD_TEXT } from '../data/marketColors';
import type { LeaderboardRow } from '../data/marketLeaderboardData';
import type { MarketCoverage } from '../data/types';
import { coverageNote, filterByCountries, maxPerformanceIndex } from '../data/utils';
import { NoData } from '../shared/NoData';
import {
  LEADERBOARD_COLUMN_HEADERS,
  LEADERBOARD_GRID_COLUMNS,
  LEADERBOARD_PRINT_GRID_COLUMNS,
  MarketLeaderboardRow,
} from './MarketLeaderboardRow';
import { TopMarketsControl, type TopMarketsSelection } from './TopMarketsControl';
import { formatWeekRange, WeekPicker } from './WeekPicker';

export function MarketLeaderboardCard({
  rows,
  resolvedWeek,
  availableWeeks,
  currencySymbol,
  selectedCountries,
  accountIds,
  isLoading,
  onWeekChange,
  topMarkets,
  coverage,
  printPage = false,
}: {
  rows: LeaderboardRow[];
  resolvedWeek: string;
  availableWeeks: string[];
  currencySymbol: string;
  selectedCountries: string[];
  accountIds: readonly number[];
  isLoading: boolean;
  onWeekChange?: (week: string) => void;
  topMarkets?: TopMarketsSelection;
  coverage?: MarketCoverage | null;
  printPage?: boolean;
}): ReactNode {
  const visibleRows = filterByCountries(rows, selectedCountries);
  const maxPerformance = maxPerformanceIndex(visibleRows);
  const medalTierByPerformance = new Map(
    [...new Set(visibleRows.map((row) => row.performanceIndex))]
      .sort((a, b) => b - a)
      .map((performance, index): [number, number] => [performance, index + 1]),
  );
  const isLatest = resolvedWeek === availableWeeks[availableWeeks.length - 1];
  // A market filter narrows the rows client-side, so the top-N coverage no longer describes them.
  const note = selectedCountries.length === 0 ? coverageNote(coverage ?? null) : null;

  return (
    <Box
      sx={(theme) => ({
        mb: printPage ? 0 : 4,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: theme.palette.border.default,
        borderRadius: `${theme.radius.xl}px`,
        boxShadow: theme.shadows[5],
        '@media print': {
          overflow: 'visible',
          ...(printPage ? { breakBefore: 'page' } : {}),
        },
      })}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
          pt: 4,
          pb: 3,
          px: 5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmojiEventsOutlined sx={{ fontSize: 16, color: GOLD_TEXT }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>
              Market Leaderboard
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
            {`Ranked by performance · ${isLatest ? 'week-over-week momentum' : 'historical snapshot'}`}
          </Typography>
          {note ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{note}</Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {topMarkets ? <TopMarketsControl {...topMarkets} /> : null}
          {availableWeeks.length > 0 &&
            (onWeekChange ? (
              <WeekPicker weeks={availableWeeks} value={resolvedWeek} onWeekChange={onWeekChange} />
            ) : (
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'warning.main',
                  bgcolor: 'warning.bg',
                  borderRadius: 1,
                  py: 1,
                  px: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                {`${formatWeekRange(resolvedWeek)}${isLatest ? ' · latest' : ''}`}
              </Typography>
            ))}
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={22} />
        </Box>
      ) : visibleRows.length === 0 ? (
        <NoData />
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: LEADERBOARD_GRID_COLUMNS,
              gap: 2,
              px: 5,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '@media print': { gridTemplateColumns: LEADERBOARD_PRINT_GRID_COLUMNS },
            }}
          >
            {LEADERBOARD_COLUMN_HEADERS.map((header, index) => (
              <Typography
                key={header}
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  textAlign: index >= 3 && index <= 5 ? 'right' : 'left',
                }}
              >
                {header}
              </Typography>
            ))}
          </Box>

          {visibleRows.map((row) => (
            <MarketLeaderboardRow
              key={row.code}
              row={row}
              maxPerformance={maxPerformance}
              medalTier={medalTierByPerformance.get(row.performanceIndex) ?? row.rank}
              currencySymbol={currencySymbol}
              accountIds={accountIds}
            />
          ))}
        </>
      )}
    </Box>
  );
}
